import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { MapPin, Clock, Users, Zap, Flame, Heart, DollarSign, ShieldCheck, Navigation, MoreHorizontal, EyeOff, BookmarkPlus, BookmarkCheck } from 'lucide-react';
import api from '../lib/api';
import { RecurringBadge } from './RecurrencePicker';

const COST_LABELS = {
  free: 'Free',
  individual: 'Pay individually',
  split: 'Split bill',
  fixed: 'Fixed amount',
  host_pays: 'Host pays',
  unknown: '',
};

function formatCostLine(plan) {
  const t = plan.cost_type;
  if (!t || t === 'unknown') return plan.cost_amount || '';
  const label = COST_LABELS[t];
  if (t === 'free') return label;
  return plan.cost_amount ? `${label} · ${plan.cost_amount}` : label;
}

function CostBadge({ plan, size = 'sm' }) {
  const text = formatCostLine(plan);
  if (!text) return null;
  const isFree = plan.cost_type === 'free';
  const px = size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-semibold ${px} ${isFree ? 'bg-[#B6E600]/20 text-[#3F5200]' : 'bg-[#F1F0EA] text-[#111111]'}`}
      data-testid="plan-cost-badge"
    >
      <DollarSign size={size === 'sm' ? 9 : 11} strokeWidth={3} />
      {text}
    </span>
  );
}

function getInitials(name) {
  return (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function formatDistance(km) {
  if (km === null || km === undefined) return null;
  if (km < 1) return `${Math.round(km * 1000)}m away`;
  return `${km.toFixed(km < 10 ? 1 : 0)}km away`;
}

function TrustBadge({ level }) {
  if (!level || level === 'new_member') return null;
  const labels = {
    trusted_host: 'Trusted',
    community_favorite: 'Favorite',
    reliable_member: 'Reliable'
  };
  return (
    <span className={`trust-badge ${level}`}>
      {labels[level] || level}
    </span>
  );
}

function formatDatePretty(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.floor((d - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff < 7) return d.toLocaleDateString('en-US', { weekday: 'long' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * CardOverflowMenu — the small "···" menu shown on every Discovery card.
 * Lets users save / unsave the plan and mark it "Not interested" so the
 * recommendation engine learns. Lives ABSOLUTELY positioned inside the
 * card so any tap inside it must stop propagation.
 *
 * Props
 * - plan: the plan being shown (used for save state + id)
 * - onDismiss: fired after a successful dismiss so the parent feed can drop the card
 * - variant: 'glass' (used on photo overlays) | 'solid' (used on white backgrounds)
 */
function CardOverflowMenu({ plan, onDismiss, variant = 'solid' }) {
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(!!plan.is_saved);
  const [busy, setBusy] = useState(false);

  const toggleSave = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    const next = !saved;
    setSaved(next);
    try {
      if (next) await api.post(`/discovery/save/${plan.id}`);
      else await api.delete(`/discovery/save/${plan.id}`);
    } catch {
      setSaved(!next); // revert
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const dismiss = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/discovery/dismiss/${plan.id}`, { reason: 'not_interested' });
      onDismiss?.(plan.id);
    } catch {}
    finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const btnClass = variant === 'glass'
    ? 'bg-black/45 backdrop-blur-md text-white hover:bg-black/60'
    : 'bg-white/90 backdrop-blur-md text-[#111111] border border-black/[0.08] hover:bg-white';

  return (
    <div
      className="absolute top-2 right-2 z-30"
      onClick={(e) => e.stopPropagation()}
      data-testid={`card-menu-${plan.id}`}
    >
      <button
        data-testid={`card-menu-trigger-${plan.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className={`w-8 h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform shadow-sm ${btnClass}`}
        aria-label="Plan options"
      >
        <MoreHorizontal size={16} strokeWidth={2.5} />
      </button>
      {open && (
        <>
          {/* tap-away catcher */}
          <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div className="absolute right-0 top-10 z-50 bg-white rounded-2xl shadow-xl shadow-black/15 border border-black/[0.06] py-1.5 min-w-[180px] animate-scale-in">
            <button
              data-testid={`card-save-${plan.id}`}
              onClick={toggleSave}
              className="w-full px-3.5 py-2.5 text-left text-[13px] font-medium text-[#111111] hover:bg-[#F7F6F2] flex items-center gap-2.5"
            >
              {saved ? <BookmarkCheck size={15} className="text-[#3F5200]" /> : <BookmarkPlus size={15} className="text-[#6B6E73]" />}
              {saved ? 'Saved' : 'Save plan'}
            </button>
            <button
              data-testid={`card-dismiss-${plan.id}`}
              onClick={dismiss}
              className="w-full px-3.5 py-2.5 text-left text-[13px] font-medium text-[#111111] hover:bg-[#F7F6F2] flex items-center gap-2.5"
            >
              <EyeOff size={15} className="text-[#6B6E73]" />
              Not interested
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Host-led card for "From People You Follow" — identity is the hero.
 * Layout: big host avatar + name + username on top, plan preview below.
 */
export function HostLedPlanCard({ plan, onDismiss }) {
  const navigate = useNavigate();
  const host = plan.host || {};
  const spotsLeft = plan.spots_left ?? (plan.capacity - (plan.approved_count || 0));
  const isFull = spotsLeft <= 0;
  const isUrgent = spotsLeft > 0 && spotsLeft <= 3;

  return (
    <div
      data-testid={`host-led-card-${plan.id}`}
      onClick={() => navigate(`/plan/${plan.id}`)}
      className="plan-card relative flex-shrink-0 w-[300px] bg-[#FFFFFF] rounded-3xl overflow-hidden border border-black/[0.06] cursor-pointer"
    >
      <CardOverflowMenu plan={plan} onDismiss={onDismiss} variant="solid" />
      {/* Host header — the hero */}
      <div className="px-4 pt-3.5 pb-3 flex items-center gap-3">
        <button
          data-testid={`host-led-host-${plan.id}`}
          onClick={(e) => { e.stopPropagation(); if (host.id) navigate(`/user/${host.id}`); }}
          className="flex-shrink-0 active:scale-95 transition-transform"
        >
          {host.profile_photo ? (
            <img loading="lazy" decoding="async" src={host.profile_photo} alt="" className="w-11 h-11 rounded-full object-cover ring-2 ring-[#B6E600]/30" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-[#F1F0EA] flex items-center justify-center text-sm font-bold text-[#9B9EA3] ring-2 ring-[#B6E600]/30">
              {getInitials(host.display_name)}
            </div>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className="text-sm font-bold text-[#111111] truncate">{host.display_name || 'Host'}</p>
            <TrustBadge level={host.trust_level} />
          </div>
          {host.username && (
            <p className="text-[11px] text-[#9B9EA3] truncate">@{host.username}</p>
          )}
          <p className="text-[10px] font-medium text-[#6B8A00] mt-0.5 flex items-center gap-1">
            <Heart size={9} className="fill-[#B6E600] text-[#B6E600]" />
            You follow {host.display_name?.split(' ')[0] || 'them'}
          </p>
        </div>
      </div>

      {/* Plan preview */}
      <div className="relative mx-3 mb-3 rounded-2xl overflow-hidden bg-[#F1F0EA]">
        {plan.cover_image ? (
          <img loading="lazy" decoding="async" src={plan.cover_image} alt={plan.title} className="w-full h-[110px] object-cover" />
        ) : (
          <div className="w-full h-[110px] bg-gradient-to-br from-[#F1F0EA] to-[#E8E7E1] flex items-center justify-center">
            <Zap size={28} className="text-[#999]" />
          </div>
        )}
      </div>

      <div className="px-4 pb-3.5">
        <p className="text-[11px] text-[#9B9EA3] uppercase tracking-wider font-semibold">
          {plan.status === 'active' && new Date(plan.date) < new Date() ? 'Created' : 'Hosted'}
        </p>
        <h3 className="font-bold text-[15px] text-[#111111] leading-snug mt-0.5 line-clamp-2">{plan.title}</h3>
        <div className="flex items-center gap-3 mt-2 text-[#6B6E73] text-[11px]">
          <span className="flex items-center gap-1">
            <Clock size={11} />
            {formatDatePretty(plan.date)} {plan.start_time}
          </span>
          <span className="flex items-center gap-1 min-w-0">
            <MapPin size={11} />
            <span className="truncate">{plan.venue || plan.city}</span>
          </span>
        </div>
        <div className="mt-2"><CostBadge plan={plan} size="sm" /></div>
        <div className="flex items-center justify-between mt-2.5">
          <div className="flex items-center gap-1 text-[11px] text-[#9B9EA3]">
            <Users size={11} />
            <span>{plan.approved_count || 0}</span>
          </div>
          <span className={`text-[11px] font-semibold ${isUrgent ? 'spots-urgent' : 'spots-normal'}`}>
            {isFull ? 'Full' : `${spotsLeft} left`}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function PlanCard({ plan, compact = false, onDismiss }) {
  const navigate = useNavigate();
  const spotsLeft = plan.spots_left ?? (plan.capacity - (plan.approved_count || 0));
  const isUrgent = spotsLeft <= 3 && spotsLeft > 0;
  const isFull = spotsLeft <= 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.floor((d - today) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff < 7) return d.toLocaleDateString('en-US', { weekday: 'long' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (compact) {
    return (
      <div
        data-testid={`plan-card-${plan.id}`}
        onClick={() => navigate(`/plan/${plan.id}`)}
        className="plan-card relative flex-shrink-0 w-[260px] bg-[#FFFFFF] rounded-2xl overflow-hidden border border-black/[0.06] cursor-pointer"
      >
        <CardOverflowMenu plan={plan} onDismiss={onDismiss} variant="glass" />
        <div className="relative h-[140px]">
          {plan.cover_image ? (
            <img loading="lazy" decoding="async" src={plan.cover_image} alt={plan.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#F1F0EA] to-[#E8E7E1] flex items-center justify-center">
              <Zap size={32} className="text-[#999]" />
            </div>
          )}
        </div>
        <div className="p-3">
          <div className="flex items-center gap-1.5">
            <h3 className="font-semibold text-sm text-[#111111] truncate flex-1">{plan.title}</h3>
            <RecurringBadge plan={plan} compact />
          </div>
          <div className="flex items-center gap-1.5 mt-1.5 text-[#6B6E73] text-xs">
            <Clock size={12} />
            <span>{formatDate(plan.date)} {plan.start_time}</span>
          </div>
          <div className="flex items-center gap-1.5 mt-1 text-[#6B6E73] text-xs">
            <MapPin size={12} />
            <span className="truncate">{plan.venue || plan.city}</span>
          </div>
          <div className="mt-2"><CostBadge plan={plan} size="sm" /></div>
          <div className="flex items-center justify-between mt-2.5">
            <div className="flex items-center gap-1.5">
              {plan.host?.profile_photo ? (
                <img loading="lazy" decoding="async" src={plan.host.profile_photo} alt="" className="w-5 h-5 rounded-full object-cover border border-[#F7F6F2]" />
              ) : (
                <div className="w-5 h-5 rounded-full bg-[#F1F0EA] flex items-center justify-center text-[8px] font-bold">
                  {getInitials(plan.host?.display_name)}
                </div>
              )}
              <span className="text-[11px] text-[#6B6E73]">{plan.host?.display_name}</span>
            </div>
            <div className="flex items-center gap-0.5">
              <Users size={12} className="text-[#9B9EA3]" />
              <span className="text-[11px] text-[#9B9EA3]">{plan.approved_count || 0}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      onClick={() => navigate(`/plan/${plan.id}`)}
      className="plan-card bg-[#FFFFFF] rounded-3xl overflow-hidden border border-black/[0.06] cursor-pointer"
    >
      <div className="relative h-[180px]">
        {plan.cover_image ? (
          <img loading="lazy" decoding="async" src={plan.cover_image} alt={plan.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#F1F0EA] to-[#E8E7E1] flex items-center justify-center">
            <Zap size={40} className="text-[#999]" />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start gap-2">
          <h3 className="font-bold text-base text-[#111111] leading-tight flex-1">{plan.title}</h3>
          <RecurringBadge plan={plan} />
        </div>

        <div className="flex items-center gap-3 mt-2.5">
          <div className="flex items-center gap-1.5 text-[#6B6E73] text-xs">
            <Clock size={13} />
            <span>{formatDate(plan.date)} {plan.start_time}</span>
          </div>
          <div className="flex items-center gap-1.5 text-[#6B6E73] text-xs">
            <MapPin size={13} />
            <span className="truncate max-w-[140px]">{plan.venue || plan.city}</span>
          </div>
        </div>

        <div className="mt-2"><CostBadge plan={plan} size="md" /></div>

        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            {plan.host?.profile_photo ? (
              <img loading="lazy" decoding="async" src={plan.host.profile_photo} alt="" className="w-7 h-7 rounded-full object-cover border-2 border-[#F7F6F2]" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-[#F1F0EA] flex items-center justify-center text-[10px] font-bold">
                {getInitials(plan.host?.display_name)}
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-[#111111]">{plan.host?.display_name}</span>
              <TrustBadge level={plan.host?.trust_level} />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {plan.attendees?.length > 0 && (
              <div className="flex -space-x-2">
                {plan.attendees.slice(0, 3).map((a, i) => (
                  <img loading="lazy" decoding="async" key={i} src={a.profile_photo} alt="" className="w-6 h-6 rounded-full object-cover border-2 border-[#FFFFFF]" />
                ))}
              </div>
            )}
            <span className={`text-xs font-medium ${isUrgent ? 'spots-urgent' : 'spots-normal'}`}>
              {isFull ? 'Full' : `${spotsLeft} left`}
            </span>
          </div>
        </div>

        {plan.hashtags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {plan.hashtags.slice(0, 3).map(tag => (
              <span key={tag} className="text-[11px] font-medium text-[#3F5200]">
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * RichPlanCard — premium full-width vertical-feed card.
 * Used by the dedicated SectionFeed page (Instagram Explore / Airbnb-style).
 * - Tall immersive cover (h-[300px])
 * - Strong typography hierarchy
 * - Host avatar + trust badge inline with name
 * - Distance, spots-left, cost, attendees stats row
 * - Trending / Filling-fast / Trusted-host signal chips on cover
 */
export function RichPlanCard({ plan, index = 0, onDismiss }) {
  const navigate = useNavigate();
  const host = plan.host || {};
  const spotsLeft = plan.spots_left ?? (plan.capacity - (plan.approved_count || 0));
  const isUrgent = spotsLeft > 0 && spotsLeft <= 3;
  const isFull = spotsLeft <= 0;
  const isTrusted = host.is_trusted_host || host.trust_level === 'trusted_host' || host.trust_level === 'community_favorite';
  const isReliable = host.is_reliable_member || host.trust_level === 'reliable_member';
  const distanceText = formatDistance(plan.distance_km);
  const attended = host.attended_count;
  const hosted = host.hosted_count;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.floor((d - today) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff < 7) return d.toLocaleDateString('en-US', { weekday: 'long' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <article
      data-testid={`rich-plan-card-${plan.id}`}
      onClick={() => navigate(`/plan/${plan.id}`)}
      className="rich-card group relative bg-[#FFFFFF] rounded-[28px] overflow-hidden border border-black/[0.06] cursor-pointer animate-fade-in"
      style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }}
    >
      <CardOverflowMenu plan={plan} onDismiss={onDismiss} variant="glass" />
      {/* Cover */}
      <div className="relative h-[280px] sm:h-[300px] overflow-hidden">
        {plan.cover_image ? (
          <img decoding="async"
            src={plan.cover_image}
            alt={plan.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#F1F0EA] to-[#E8E7E1] flex items-center justify-center">
            <Zap size={48} className="text-[#999]" />
          </div>
        )}

        {/* Top-right: distance only — unique non-duplicated info.
            Trending / Filling-fast / Full removed: those signals are already
            visible in the body stats row, no need to clutter the photo. */}
        {distanceText && (
          <div className="absolute top-3 left-3 pointer-events-none">
            <span className="rich-chip rich-chip-glass">
              <Navigation size={11} strokeWidth={2.5} /> {distanceText}
            </span>
          </div>
        )}

        {/* Bottom gradient + title overlay */}
        <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 px-5 pb-4">
          <div className="flex items-center gap-2 mb-2 text-[11px] text-white/85 font-medium">
            <Clock size={12} strokeWidth={2.5} />
            <span>{formatDate(plan.date)}{plan.start_time ? ` · ${plan.start_time}` : ''}</span>
            <span className="opacity-60">•</span>
            <MapPin size={12} strokeWidth={2.5} />
            <span className="truncate max-w-[160px]">
              {plan.venue || plan.area || plan.region || plan.city}
              {plan.region && plan.region !== (plan.venue || plan.area) && plan.venue ? `, ${plan.region}` : ''}
            </span>
          </div>
          <h3 className="text-white text-[22px] leading-[1.15] font-extrabold tracking-tight line-clamp-2 drop-shadow-sm">
            {plan.title}
          </h3>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 pt-4">
        {/* Host row */}
        <button
          data-testid={`rich-plan-host-${plan.id}`}
          onClick={(e) => { e.stopPropagation(); if (host.id) navigate(`/user/${host.id}`); }}
          className="flex items-center gap-3 w-full text-left active:scale-[0.99] transition-transform"
        >
          {host.profile_photo ? (
            <img loading="lazy" decoding="async"
              src={host.profile_photo}
              alt=""
              className={`w-10 h-10 rounded-full object-cover ${isTrusted ? 'ring-2 ring-[#B6E600]/50' : ''}`}
            />
          ) : (
            <div className={`w-10 h-10 rounded-full bg-[#F1F0EA] flex items-center justify-center text-xs font-bold text-[#9B9EA3] ${isTrusted ? 'ring-2 ring-[#B6E600]/50' : ''}`}>
              {getInitials(host.display_name)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-[13.5px] font-bold text-[#111111] truncate">{host.display_name || 'Host'}</span>
              {isTrusted && (
                <span className="trust-pill trust-pill-trusted" data-testid="trust-pill-trusted">
                  <ShieldCheck size={10} strokeWidth={2.5} /> Trusted host
                </span>
              )}
              {!isTrusted && isReliable && (
                <span className="trust-pill trust-pill-reliable" data-testid="trust-pill-reliable">
                  <ShieldCheck size={10} strokeWidth={2.5} /> Reliable
                </span>
              )}
            </div>
            <div className="text-[11px] text-[#9B9EA3] truncate flex items-center gap-2">
              {hosted > 0 && <span>{hosted} hosted</span>}
              {hosted > 0 && attended > 0 && <span className="opacity-50">·</span>}
              {attended > 0 && <span>{attended} attended</span>}
              {!hosted && !attended && host.username && <span>@{host.username}</span>}
            </div>
          </div>
        </button>

        {/* Stats row */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-black/[0.05]">
          <div className="flex items-center gap-4 text-[12px]">
            <div className="flex items-center gap-1.5 text-[#6B6E73]">
              <Users size={13} strokeWidth={2.4} />
              <span className="font-semibold text-[#111111]">{plan.approved_count || 0}</span>
              <span className="text-[#9B9EA3]">going</span>
            </div>
            {!isFull && (
              <div className={`flex items-center gap-1.5 ${isUrgent ? 'spots-urgent' : 'text-[#6B6E73]'}`}>
                <Flame size={13} strokeWidth={2.4} className={isUrgent ? '' : 'opacity-60'} />
                <span className="font-semibold">{spotsLeft}</span>
                <span className={isUrgent ? '' : 'text-[#9B9EA3]'}>left</span>
              </div>
            )}
            {isFull && <span className="text-[12px] font-semibold text-[#FF3B30]">Full</span>}
          </div>
          <CostBadge plan={plan} size="md" />
        </div>

        {/* Attendees row (if any) */}
        {plan.attendees?.length > 0 && (
          <div className="flex items-center gap-2 mt-3">
            <div className="flex -space-x-2">
              {plan.attendees.slice(0, 4).map((a, i) => (
                a.profile_photo
                  ? <img loading="lazy" decoding="async" key={i} src={a.profile_photo} alt="" className="w-6 h-6 rounded-full object-cover border-2 border-white" />
                  : <div key={i} className="w-6 h-6 rounded-full bg-[#F1F0EA] border-2 border-white text-[8px] font-bold flex items-center justify-center text-[#9B9EA3]">{getInitials(a.display_name)}</div>
              ))}
            </div>
            <span className="text-[11px] text-[#9B9EA3]">
              {plan.attendees[0]?.display_name?.split(' ')[0]}{plan.approved_count > 1 ? ` and ${plan.approved_count - 1} other${plan.approved_count - 1 > 1 ? 's' : ''}` : ''} going
            </span>
          </div>
        )}
      </div>
    </article>
  );
}
